import React, { useState, useEffect, useRef, memo } from 'react';

// --- ICONO DE CONFIGURACIÓN / SETTINGS EN SVG ---
const IconSettings = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
    </svg>
);

/**
 * ColumnSelector - Selector de columnas interactivo, autocontenido y memoizado.
 * Gestiona de forma interna su estado de apertura y se cierra automáticamente al hacer clic fuera.
 * * @param {object} columnVisibility - Mapa de visibilidad de las columnas { [key]: boolean }.
 * @param {object} columnLabels - Etiquetas de traducción legibles para cada columna { [key]: string }.
 * @param {function} toggleColumn - Callback para cambiar el estado de visibilidad de una columna.
 * @param {string} align - Alineación del menú desplegable ('right' | 'left'). Por defecto: 'right'.
 * @param {string} buttonClassName - Clases CSS adicionales para personalizar el botón disparador.
 */
const ColumnSelector = memo(({
    columnVisibility,
    columnLabels,
    toggleColumn,
    align = 'right',
    buttonClassName = ""
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    // --- ESCUCHADOR CLICK-OUTSIDE ---
    // Detecta clics fuera del contenedor del componente para cerrar el panel de manera natural.
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Clases dinámicas de alineación
    const alignClass = align === 'right' ? 'right-0' : 'left-0';

    return (
        <div ref={containerRef} className="relative inline-block text-left">
            {/* Botón Disparador */}
            <button
                type="button"
                onClick={() => setIsOpen(prev => !prev)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border font-bold text-xs transition-all shadow-sm select-none ${
                    isOpen 
                        ? 'bg-slate-800 text-white border-slate-800' 
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                } ${buttonClassName}`}
            >
                <IconSettings /> Columnas
            </button>
            
            {/* Panel Flotante de Visibilidad */}
            {isOpen && (
                <div className={`absolute ${alignClass} mt-2 w-64 bg-white border border-slate-200 rounded-2xl shadow-2xl z-[1000] p-4 animate-fade-in text-left`}>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 px-2 select-none">
                        Visibilidad de Columnas
                    </p>
                    
                    <div className="space-y-1.5 max-h-80 overflow-y-auto custom-scrollbar pr-1">
                        {Object.keys(columnVisibility).map(key => (
                            <label 
                                key={key} 
                                className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors group select-none"
                            >
                                <input
                                    type="checkbox"
                                    checked={columnVisibility[key]}
                                    onChange={() => toggleColumn(key)}
                                    className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                                />
                                <span className="text-xs font-bold text-slate-600 group-hover:text-slate-900 transition-colors">
                                    {columnLabels[key] || key}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
});

ColumnSelector.displayName = 'ColumnSelector';

export default ColumnSelector;